import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const links = [
  { to: "/", label: "Holdings", testId: "nav-home" },
  { to: "/logistics", label: "Logistics", testId: "nav-logistics" },
  { to: "/properties", label: "Properties", testId: "nav-properties" },
  { to: "/government", label: "Government", testId: "nav-government" },
  { to: "/partnerships", label: "Partnerships", testId: "nav-partnerships" },
  { to: "/diversity", label: "Diversity", testId: "nav-diversity" },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo({ top: 0 }); }, [location.pathname]);

  return (
    <header
      data-testid="primary-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/85 backdrop-blur-md border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Logo variant="nav" />

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              end={l.to === "/"}
              onClick={() => window.dispatchEvent(new Event("tth:flash"))}
              className={({ isActive }) =>
                `overline text-[11px] py-1 border-b transition-colors ${
                  isActive ? "text-white border-white" : "text-white/60 hover:text-white border-transparent hover:border-white/40"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-contact-cta"
            onClick={() => window.dispatchEvent(new Event("tth:flash"))}
            className="overline text-[11px] px-5 py-3 bg-white text-black hover:bg-white/85 transition-colors"
          >
            Contact
          </Link>
        </nav>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="lg:hidden bg-black/95 backdrop-blur-md border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                end={l.to === "/"}
                onClick={() => window.dispatchEvent(new Event("tth:flash"))}
                className={({ isActive }) =>
                  `overline py-3 border-b border-white/10 ${isActive ? "text-white" : "text-white/65"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-contact-cta-mobile"
              onClick={() => window.dispatchEvent(new Event("tth:flash"))}
              className="overline mt-4 px-5 py-4 bg-white text-black text-center"
            >
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
